<template>
  <t-card
    class="border rounded-lg"
    :class="isClickable ? 'cursor-pointer transition-colors hover:bg-gray-50 active:bg-gray-100' : ''"
    @click="onClick"
  >
    <div
      class="flex gap-4 w-full items-center">
      <div
        class="rounded-full border border-solid border-gray-200 h-12 w-12 flex justify-center items-center bg-[#C4B0EE20]">
        <i
          class="pi text-[#723BE3] p-4"
          :class="{
              'pi-calendar': notification.type === 'visits',
              'pi-money-bill': notification.type === 'payment',
              'pi-comment': notification.type === 'review',
              'pi-megaphone': notification.type === 'smart-nudge',
              'pi-shopping-bag': notification.type === 'orders',
              'pi-check-square': notification.type === 'tasks',
            }"/>
      </div>
      <div class="flex flex-col flex-grow min-w-0">
        <span class="text-sm font-medium">{{ notification.title }}</span>
        <span class="text-sm mt-1">{{ notification.description }}</span>
        <span class="text-xs text-gray-300 mt-2">לפני {{ timePassed(notification.dateAdded as string) }}</span>
      </div>
      <!-- Arrow hints that tapping opens the appointment on the calendar / the orders tab -->
      <i v-if="isClickable" class="pi pi-chevron-left text-gray-400 !text-xs flex-none" />
    </div>
  </t-card>
</template>

<script setup lang="ts">
import {computed, onUnmounted, PropType} from "vue";
import {IAdminAppNotification} from "@torim-app/types";
import {DateTime} from "luxon";
import {SharedApi} from "@app/api/admin";
import { trackEvent } from '@app/composables/posthog';
import { useRouter } from 'vue-router';
import { AdminRouteNames } from '@app/router/route-names';
import { useDrawer } from '@torim-app/shared-components';

const props = defineProps({
  notification: {type: Object as PropType<IAdminAppNotification & { _id: string }>, required: true}
})

const router = useRouter();
const { closeDrawer } = useDrawer();

// Only appointment notifications carry an appointmentId; those deep-link to the calendar.
const isAppointmentLink = computed(() => !!props.notification.appointmentId);
// Product-order notifications deep-link to the shop's orders tab.
const isOrderLink = computed(() => props.notification.type === 'orders');
const isClickable = computed(() => isAppointmentLink.value || isOrderLink.value);

// Orders tab index within the shop page's TabsWrapper (מוצרים / הזמנות / הגדרות).
const ORDERS_TAB_INDEX = 1;

const timePassed = (date: string) => {
  const minutes = Math.abs(DateTime.fromISO(date).diffNow("minute").minutes);
  if (minutes < 60) return `${Math.round(minutes)} דקות`;
  else if (minutes < 1440) return `כ-${Math.floor(minutes / 60)} שעות`
  else return `${Math.floor(minutes / 1440)} ימים`
}

const onClick = () => {
  if (!isClickable.value) return;
  closeDrawer();

  // Order notifications open the shop's orders tab.
  if (isOrderLink.value) {
    trackEvent('notification_order_clicked');
    router.push({ name: AdminRouteNames.Shop, query: { tab: String(ORDERS_TAB_INDEX) } });
    return;
  }

  // Same mechanism as a push-notification tap: navigate to Home with the highlight query,
  // which makes the calendar jump to the date and pulse the appointment (see Home.vue).
  trackEvent('notification_appointment_clicked', { type: props.notification.type });
  router.push({
    name: AdminRouteNames.Home,
    query: {
      highlightAppointmentId: props.notification.appointmentId!,
      ...(props.notification.appointmentDate ? { appointmentDate: props.notification.appointmentDate } : {}),
      ...(props.notification.providerId ? { providerId: props.notification.providerId } : {}),
    },
  });
}

onUnmounted(() => {
  if (!props.notification.viewedAt) {
    trackEvent('notification_read', { type: props.notification.type });
    SharedApi.markNotificationAsRead(props.notification._id);
  }
})

</script>

<style scoped lang="scss">

</style>
